import { defineBoot } from '#q-app'
import axios from 'axios'
import { Notify, Platform } from 'quasar'
import { useAuthStore } from '@/stores/auth'
import { useLocationStore } from '@/stores/location'
import { usePosDataStore } from '@/stores/posData'
import { useRegisterStore } from '@/stores/register'
import { resolvePosClientInstanceId } from '@/utils/posClientInstanceId'
import { perfMark, perfLogMeasure } from '@/utils/perfTiming'
import { getActivePinia } from 'pinia'
import {
  LS_CAPACITOR_SUBDOMAIN,
  normalizeShellSubdomain,
  isValidShellSubdomain
} from '@/config/electronShellStorage'
import { MOCK_CARD_FLOW_REQUEST_HEADER } from '@/config/mockCardFlow'
import { isMockCardFlowActive } from '@/utils/mockCardFlow'
import { apiEndpointManager, isTransportLayerError } from '../services/apiEndpointManager'

const DEFAULT_CAPACITOR_DOMAIN_SUFFIX = import.meta.env.API_DOMAIN_SUFFIX || 'bluapps.dev'
const buildApiBase = (url) => `${String(url || '').replace(/\/+$/, '')}/api`

const isElectronShell = () =>
  !!Platform.is.electron ||
  (typeof window !== 'undefined' && !!window?.electronAPI?.isElectron)

/** Laravel CSRF: Axios reads XSRF-TOKEN from document.cookie - invisible for API host from http://127.0.0.1; main reads the jar. */
const useElectronMainProcessXsrf = () => isElectronShell()

/** Per API origin cache populated after /sanctum/csrf-cookie and IPC reads. */
const electronXsrfCache = new Map()

function decodeXsrfCookieValue (raw) {
  if (typeof raw !== 'string' || !raw) return null
  try {
    return decodeURIComponent(raw)
  } catch {
    return raw
  }
}

function setXsrfHeader (headers, token) {
  if (!token) return false
  headers['X-XSRF-TOKEN'] = token
  return true
}

async function fetchElectronXsrfToken (origin, { useCache = true } = {}) {
  if (!origin) return null
  if (useCache && electronXsrfCache.has(origin)) {
    return electronXsrfCache.get(origin)
  }
  if (typeof window === 'undefined' || !window.electronAPI?.getXsrfTokenForApiUrl) {
    return null
  }
  const r = await window.electronAPI.getXsrfTokenForApiUrl(origin)
  if (!r?.success || typeof r.value !== 'string' || !r.value) {
    return null
  }
  const decoded = decodeXsrfCookieValue(r.value)
  if (decoded) {
    electronXsrfCache.set(origin, decoded)
  }
  return decoded
}

async function primeElectronXsrfCache (origin) {
  if (!useElectronMainProcessXsrf() || !origin) return
  const first = await fetchElectronXsrfToken(origin, { useCache: false })
  if (first) return first
  await new Promise((resolve) => setTimeout(resolve, 50))
  return fetchElectronXsrfToken(origin, { useCache: false })
}

function logElectronXsrf (level, message, details) {
  if (!import.meta.env.QUASAR_DEV || !useElectronMainProcessXsrf()) return
  const fn = level === 'warn' ? console.warn : console.debug
  fn(`[axios] ${message}`, details ?? '')
}

function buildElectronCsrfError (origin, ipcError) {
  const err = new Error('Electron CSRF header unavailable')
  err.code = 'electron_csrf_header_missing'
  err.isElectronCsrfError = true
  err.apiOrigin = origin ?? null
  err.ipcError = ipcError ?? null
  return err
}

function requestNeedsXsrfHeader (config) {
  return METHODS_NEEDING_XSRF_BODY.has(getMethod(config))
}

function isSanctumCsrfCookieRequest (config) {
  const path = String(config?.url || '').split('?')[0]
  return path.includes('/sanctum/csrf-cookie')
}

function resolveRequestApiOrigin (config) {
  try {
    const base = String(config.baseURL ?? api.defaults.baseURL ?? '').replace(/\/+$/, '')
    const rel = String(config.url ?? '').replace(/^\//, '')
    if (!base && !rel) return null
    const combined = /^https?:\/\//i.test(rel) ? rel : (rel ? `${base}/${rel}` : base)
    return new URL(combined).origin
  } catch {
    return null
  }
}

const METHODS_NEEDING_XSRF_BODY = new Set(['post', 'put', 'patch', 'delete'])

async function applyElectronXsrfToken (config) {
  if (!useElectronMainProcessXsrf()) return
  if (!requestNeedsXsrfHeader(config) || isSanctumCsrfCookieRequest(config)) return

  const origin = resolveRequestApiOrigin(config)
  if (typeof window === 'undefined' || !window.electronAPI?.getXsrfTokenForApiUrl) {
    logElectronXsrf('warn', 'electronAPI.getXsrfTokenForApiUrl is unavailable — check electron-preload.cjs is loaded')
    throw buildElectronCsrfError(origin, 'electron_api_missing')
  }

  config.headers = config.headers ?? {}
  const headers = config.headers
  if (headers['X-XSRF-TOKEN'] ?? headers['x-xsrf-token']) return

  if (!origin) {
    logElectronXsrf('warn', 'Could not resolve API origin for CSRF header', config?.url)
    return
  }

  let token = await fetchElectronXsrfToken(origin)
  if (!token) {
    token = await primeElectronXsrfCache(origin)
  }
  if (setXsrfHeader(headers, token)) return

  logElectronXsrf('warn', 'Failed to read XSRF-TOKEN from Electron cookie jar', { origin })
  if (config.meta?.requireElectronCsrf !== false) {
    throw buildElectronCsrfError(origin, 'not_found')
  }
}

/** file:// cannot use path redirects; packaged Electron uses http://127.0.0.1 (see electron-main). */
const redirectToLogin = () => {
  if (typeof window === 'undefined') return
  if (window.location.protocol === 'file:') {
    window.location.hash = '#/login'
    return
  }
  window.location.href = '/login'
}

/** Capacitor or Electron: API host must come from stored subdomain (or dev API_URL fallback), not window.location. */
const usesStoredTenantSubdomain = () => !!Platform.is.capacitor || isElectronShell()

const readCapacitorSubdomain = () => {
  if (typeof window === 'undefined') return ''
  return normalizeShellSubdomain(window.localStorage.getItem(LS_CAPACITOR_SUBDOMAIN))
}

const resolveStoredTenantApiUrl = () => {
  const managed = apiEndpointManager.getBaseUrl()
  if (managed) {
    return managed
  }

  const subdomain = readCapacitorSubdomain()
  if (!subdomain || !isValidShellSubdomain(subdomain)) return null
  return buildApiBase(`https://${subdomain}.${DEFAULT_CAPACITOR_DOMAIN_SUFFIX}`)
}

// Get the current hostname and construct the API URL
const getApiUrl = () => {
  if (usesStoredTenantSubdomain()) {
    const tenantApiUrl = resolveStoredTenantApiUrl()
    if (tenantApiUrl) {
      return tenantApiUrl
    }

    if (import.meta.env.API_URL) {
      return buildApiBase(import.meta.env.API_URL)
    }
  }

  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // If we're in development, use the configured API URL
  if (import.meta.env.QUASAR_DEV) {
    return buildApiBase(import.meta.env.API_URL || 'http://localhost:8000')
  }

  // In production, use the current subdomain
  return `${protocol}//${hostname}/api`
}

export const getCapacitorApiSubdomain = () => readCapacitorSubdomain()

export const setCapacitorApiSubdomain = (value) => {
  if (typeof window === 'undefined') return
  const subdomain = normalizeShellSubdomain(value)

  if (!subdomain) {
    window.localStorage.removeItem(LS_CAPACITOR_SUBDOMAIN)
    apiEndpointManager.resetForSubdomainChange()
    api.defaults.baseURL = getApiUrl()
    if (typeof window !== 'undefined' && isElectronShell() && window.electronAPI?.mergeShellUserSettings) {
      void window.electronAPI.mergeShellUserSettings({ apiSubdomain: '' })
    }
    return
  }

  if (!isValidShellSubdomain(subdomain)) {
    throw new Error('Invalid subdomain format')
  }

  window.localStorage.setItem(LS_CAPACITOR_SUBDOMAIN, subdomain)
  apiEndpointManager.resetForSubdomainChange()
  api.defaults.baseURL = getApiUrl()
  if (typeof window !== 'undefined' && isElectronShell() && window.electronAPI?.mergeShellUserSettings) {
    void window.electronAPI.mergeShellUserSettings({ apiSubdomain: subdomain })
  }
}

// Create axios instance with default config
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 30000,
  withCredentials: true,
  // Electron: XSRF cookie is on API origin; renderer cannot read it from loopback. See applyElectronXsrfToken.
  withXSRFToken: !useElectronMainProcessXsrf(),
  xsrfCookieName: 'XSRF-TOKEN',
  xsrfHeaderName: 'X-XSRF-TOKEN',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

const isOfflineNow = () => typeof navigator !== 'undefined' && navigator.onLine === false

const getMethod = (cfg) => String(cfg?.method || 'get').toLowerCase()

const deriveMessage = (error) => {
  const cfg = error?.config
  const meta = cfg?.meta || {}

  if (typeof meta.toastMessage === 'string' && meta.toastMessage.trim()) {
    return meta.toastMessage.trim()
  }

  const data = error?.response?.data
  // Common Laravel-ish shapes
  const msg =
    data?.message ||
    data?.error ||
    data?.errors?.message ||
    null

  if (typeof msg === 'string' && msg.trim()) return msg.trim()

  // Axios network errors
  if (!error?.response && error?.message) return String(error.message)

  return 'Something went wrong. Please try again.'
}

// Request interceptor (no token handling needed with Sanctum)
api.interceptors.request.use(
  async (config) => {
    // When offline, short-circuit API requests to avoid noisy console/network errors.
    // Callers should handle this (often by falling back to Dexie caches).
    if (isOfflineNow()) {
      const err = new Error('Offline')
      err.isOffline = true
      err.config = config
      return Promise.reject(err)
    }

    try {
      await applyElectronXsrfToken(config)
    } catch (csrfErr) {
      return Promise.reject(csrfErr)
    }

    const path = String(config.url || '').split('?')[0]
    const resolvedBase = apiEndpointManager.getBaseUrl()
    if (resolvedBase && config.baseURL !== resolvedBase) {
      config.baseURL = resolvedBase
    }

    if (isMockCardFlowActive() && path.startsWith('/payments')) {
      config.headers = config.headers || {}
      config.headers[MOCK_CARD_FLOW_REQUEST_HEADER] = '1'
    }
    if (path.startsWith('/cart') || path.startsWith('/suspended-sales')) {
      config.headers = config.headers || {}
      if (!config.headers['X-Client-Instance-Id']) {
        config.headers['X-Client-Instance-Id'] = await resolvePosClientInstanceId()
      }
      try {
        const pinia = getActivePinia()
        if (pinia) {
          const rid = useRegisterStore(pinia).currentRegister?.id
          if (rid && !config.headers['X-Sales-Register-Id']) {
            config.headers['X-Sales-Register-Id'] = String(rid)
          }
        }
      } catch {
        // Pinia may be unavailable very early in boot
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor
api.interceptors.response.use(
  async (response) => {
    void apiEndpointManager.preferPrimaryWhenHealthy()

    if (isSanctumCsrfCookieRequest(response?.config)) {
      const origin = resolveRequestApiOrigin(response.config)
      if (origin) {
        electronXsrfCache.delete(origin)
        await primeElectronXsrfCache(origin)
      }
    }
    return response
  },
  async (error) => {
    // If we're offline (or request was short-circuited above), don't log as an error.
    if (error?.isOffline || (!error?.response && isOfflineNow())) {
      return Promise.reject(error)
    }

    try {
      await apiEndpointManager.evaluateTransportFailure(error)
      const nextBase = apiEndpointManager.getBaseUrl()
      if (
        nextBase &&
        error?.config &&
        !error.config.__apiEndpointRetried &&
        isTransportLayerError(error) &&
        ['get', 'head'].includes(getMethod(error.config))
      ) {
        error.config.__apiEndpointRetried = true
        error.config.baseURL = nextBase
        return api.request(error.config)
      }
    } catch {
      // Never block normal error handling if failover logic fails
    }

    if (error.response?.status === 401) {
      const authStore = useAuthStore()

      // Only logout if we're actually authenticated to prevent boot loops
      if (authStore.isAuthenticated) {
        await authStore.logout()
        redirectToLogin()
      }

      // Don't log 401 errors as they're expected when not authenticated
      return Promise.reject(error)
    }

    if (error.response?.status === 419 && useElectronMainProcessXsrf()) {
      logElectronXsrf('warn', 'Server returned 419 — CSRF header likely missing or stale', {
        url: error?.config?.url,
        hadHeader: !!(error?.config?.headers?.['X-XSRF-TOKEN'] ?? error?.config?.headers?.['x-xsrf-token'])
      })
      const origin = resolveRequestApiOrigin(error?.config)
      if (origin) {
        electronXsrfCache.delete(origin)
      }
      error.isCsrfMismatch = true
    }

    if (error?.isElectronCsrfError) {
      logElectronXsrf('warn', 'Blocked request: Electron CSRF header could not be prepared', {
        apiOrigin: error.apiOrigin,
        ipcError: error.ipcError
      })
    }

    // Global API error handling (UI notifications)
    try {
      const cfg = error?.config
      const meta = cfg?.meta || {}
      const method = getMethod(cfg)
      const isGet = method === 'get'

      // Policy:
      // - GET: no toast by default
      // - Non-GET: toast by default
      // - Overrides:
      //   - meta.silent === true => never toast
      //   - meta.toast === true => force toast (even for GET)
      const shouldToast = meta.silent
        ? false
        : (meta.toast ? true : !isGet)

      if (shouldToast) {
        const message = deriveMessage(error)
        Notify.create({
          type: 'negative',
          message
        })
      }
    } catch {
      // Never block error propagation if notification logic fails
    }

    return Promise.reject(error)
  }
)

export default defineBoot(async () => {
  perfMark('axios-boot-start')

  // Check auth status on app initialization to handle session restoration
  const authStore = useAuthStore()
  const locationStore = useLocationStore()
  const posDataStore = usePosDataStore()
  const registerStore = useRegisterStore()

  // Pre-hydrate auth state from cache for Phase 1 offline read-only support.
  try {
    await authStore.hydrateFromCache()
    // Optimistic locale: if we have cached user and localStorage has matching override, set i18n immediately
    if (authStore.user?.id && typeof window !== 'undefined') {
      try {
        const raw = window.localStorage.getItem('blu_locale')
        if (raw) {
          const parsed = JSON.parse(raw)
          if (parsed?.userId === authStore.user.id && parsed?.locale) {
            const { i18n } = await import('@/boot/i18n')
            const valid = ['en', 'es'].includes(parsed.locale) ? parsed.locale : 'en'
            i18n.global.locale.value = valid
          }
        }
      } catch {
        // ignore
      }
    }
  } catch (e) {
    console.warn('Auth hydration from cache failed:', e)
  }

  // Only restore an authenticated session when cache already indicates a user.
  // Unauthenticated cold starts (e.g. /login) skip /auth/check here; the router
  // guard performs a single session probe when needed.
  if (authStore.isAuthenticated) {
      try {
        await authStore.checkAuth()

        // Warm the Sales Register chunk so first navigation to POS is faster.
        void import('@/pages/SalesRegisterPage.vue')

      // Hydrate POS data from local snapshot first, so UI can be ready
      // even before the network refresh completes.
      try {
        await posDataStore.hydrateFromSnapshot()
      } catch (e) {
        console.error('POS data hydration from cache failed:', e)
      }

      // Phase 1: hydrate locations/register overview so core POS navigation works offline.
      try {
        await locationStore.hydrateFromCache()
      } catch (e) {
        console.warn('Location hydration from cache failed:', e)
      }
      try {
        await registerStore.hydrateOverviewFromCache({ locationId: locationStore.getCurrentLocation?.id })
      } catch (e) {
        console.warn('Register hydration from cache failed:', e)
      }

      // Sync POS catalog in the background when stale (refreshes categories,
      // layouts, and grid products; only stamps revision after a real reload).
      try {
        const preloadPromise = posDataStore.syncPosCatalogIfStale({ activeScope: 'user' })

        preloadPromise.catch((e) => {
          console.error('POS catalog sync during boot failed:', e)
        })
      } catch (e) {
        console.error('Failed to kick off POS catalog sync:', e)
      }
    } catch {
      // Silently fail - user is not authenticated
    }
  }

  perfMark('axios-boot-end')
  perfLogMeasure('axios-boot', 'axios-boot-start', 'axios-boot-end')
})

export { api }
